import { Button } from '@components/Button/Button';
import type { ReportFiltersValues } from '@components/ReportFilters/reportFilters.types';
import {
  buildEmptyReportFilters,
  hasActiveReportFilters,
} from '@components/ReportFilters/reportFilters.utils';
import { ReportFiltersPanel } from '@components/ReportFilters/ReportFiltersPanel/ReportFiltersPanel';
import { Ionicons } from '@expo/vector-icons';
import { useAppTranslation } from '@hooks/useAppTranslation';
import { useReportFiltersConfig } from '@hooks/useReportFiltersConfig';
import { useMemo, useState, type ComponentProps } from 'react';
import { useTheme, XStack } from 'tamagui';

interface UseReportFiltersPanelOptions {
  isSmallScreen: boolean;
  buttonSize?: ComponentProps<typeof Button>['size'];
}

interface UseReportFiltersPanelResult {
  appliedFilters: ReportFiltersValues;
  filterButton: React.ReactElement;
  filtersPanel: React.ReactElement | null;
  isConfigLoading: boolean;
}

export const useReportFiltersPanel = ({
  isSmallScreen,
  buttonSize,
}: UseReportFiltersPanelOptions): UseReportFiltersPanelResult => {
  const { t } = useAppTranslation();
  const { config, isLoading } = useReportFiltersConfig();
  const theme = useTheme();

  const [filtersOpen, setFiltersOpen] = useState(false);
  const [appliedFilters, setAppliedFilters] = useState<ReportFiltersValues>(
    buildEmptyReportFilters(),
  );

  const emptyFilters = useMemo(() => buildEmptyReportFilters(), []);
  const hasFiltersApplied = hasActiveReportFilters(
    appliedFilters,
    emptyFilters,
  );

  const handleApplyFilters = (values: ReportFiltersValues): void => {
    setAppliedFilters(values);
    setFiltersOpen(false);
  };

  const filterButton = (
    <XStack>
      <Button
        variant="outlined"
        title={isSmallScreen ? undefined : t('filters.open')}
        onPress={() => setFiltersOpen((prev) => !prev)}
        icon={<Ionicons name="options" size={18} color={theme.color?.val} />}
        padding={isSmallScreen ? '$2' : undefined}
        accessibilityLabel={t('filters.open')}
        size={buttonSize}
      />
      {hasFiltersApplied && (
        <XStack
          position="absolute"
          top={-5}
          right={-5}
          width={10}
          height={10}
          borderRadius="$circle"
          backgroundColor="$brand"
          pointerEvents="none"
        />
      )}
    </XStack>
  );

  const filtersPanel = (
    <ReportFiltersPanel
      config={config}
      defaultValues={appliedFilters}
      open={filtersOpen}
      variant={isSmallScreen ? 'modal' : 'side'}
      onApply={handleApplyFilters}
      onClose={() => setFiltersOpen(false)}
    />
  );

  return {
    appliedFilters,
    filterButton,
    filtersPanel,
    isConfigLoading: isLoading,
  };
};
